import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    HStack,
    IconButton,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import InlineErrorState from '../../components/states/InlineErrorState';
import { translate } from '../../i18n';
import { DayOfWeek } from '../../models/court/dto/batch-upsert-availabilities.dto';
import { TimeWindowDto } from '../../models/time-window/dto/time-window.dto';
import { READ_FAILED_I18N_KEY, classifyRead } from '../_shared/readState';
import MultiDayOfWeekPicker from '../court-management/components/MultiDayOfWeekPicker';
import RangePicker from '../court-management/components/RangePicker';
import { useBatchUpsertTimeWindows } from './hooks/useBatchUpsertTimeWindows';
import { useGetTimeWindows } from './hooks/useGetTimeWindows';
import { getTimeRangeKey, numberToHourMinuteLabel, timeToSliderValue } from './utils/time-window.utils';

interface TimeWindowFormProps {
    groupId: string;
    onSuccess: () => void;
}

interface TimeWindowRow {
    id: string;
    daysOfWeek: DayOfWeek[];
    timeRange: [number, number];
}

const createDefaultTimeWindow = (): TimeWindowRow => ({
    id: crypto.randomUUID(),
    timeRange: [16, 36] as [number, number],
    daysOfWeek: [1] as DayOfWeek[],
});

const groupTimeWindowsByTimeRange = (timeWindows: TimeWindowDto[]): TimeWindowRow[] => {
    const groups = new Map<string, TimeWindowRow>();

    timeWindows.forEach(window => {
        const startTime = new Date(window.startTime);
        const endTime = new Date(window.endTime);
        const startValue = timeToSliderValue(startTime);
        const endValue = timeToSliderValue(endTime);
        const dayOfWeek = startTime.getDay() as DayOfWeek;
        const timeRange: [number, number] = [startValue, endValue];
        const key = getTimeRangeKey(timeRange);

        if (!groups.has(key)) {
            groups.set(key, {
                id: crypto.randomUUID(),
                timeRange,
                daysOfWeek: [dayOfWeek],
            });
        } else {
            const group = groups.get(key)!;
            if (!group.daysOfWeek.includes(dayOfWeek)) {
                group.daysOfWeek.push(dayOfWeek);
            }
        }
    });

    return Array.from(groups.values());
};

export function TimeWindowForm({ groupId, onSuccess }: TimeWindowFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [timeWindows, setTimeWindows] = useState<TimeWindowRow[]>([]);
    // R127-1. This form is not an "add" form despite the modal that opens it
    // being labelled one: `PUT time-windows/groups/:id/windows` does
    // `nativeDelete(WeeklyTimeWindow, { group })` and then re-creates from the
    // body, so whatever is on screen when Save is pressed becomes the group's
    // ENTIRE schedule. The read below is therefore not decoration — it is the
    // payload.
    //
    // It used to be unclassified: `{ data: existingTimeWindows, isLoading }`,
    // with the seeding effect falling through to `else if (!isLoading)` on a
    // refused read. `isLoading` is false once a query errors, so a 403/5xx on
    // `GET .../windows` seeded the form with ONE INVENTED ROW (Monday,
    // 08:00-18:00) that is indistinguishable from a group that really has one
    // window — and pressing Save then deleted every real window in the group
    // and replaced them with that invention. A read failure producing a
    // DESTRUCTIVE write, which is a step past run 126's `OrganizationPage`:
    // there the wrong affordance invited a create, here it pre-fills the
    // discard.
    //
    // `classifyRead` separates the three worlds the old boolean could not:
    // `answered` (seed from the server, or offer the default row for a group
    // that genuinely has none), `loading`, and everything else — where the form
    // itself, and therefore the Save button, must not render at all. R96-3 is
    // free here: `unreadable` requires `!data`, so a failed BACKGROUND refetch
    // over a schedule already on screen keeps the operator's edits.
    const {
        data: existingTimeWindows,
        isError: isTimeWindowsError,
        isInitialLoading,
    } = useGetTimeWindows(groupId);
    const readState = classifyRead(isTimeWindowsError, isInitialLoading, existingTimeWindows);

    useEffect(() => {
        // Seeded ONLY from an answered read. An empty answer is a real fact
        // about the group — it has no windows — and the default row is the
        // right offer there, and only there.
        if (readState !== 'answered' || !existingTimeWindows) return;
        setTimeWindows(
            existingTimeWindows.length > 0
                ? groupTimeWindowsByTimeRange(existingTimeWindows)
                : [createDefaultTimeWindow()],
        );
    }, [readState, existingTimeWindows]);

    const batchUpsertMutation = useBatchUpsertTimeWindows(groupId, () => {
        onSuccess();
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Flatten all time windows from all rows
            const allTimeWindows = timeWindows.flatMap(row => {
                const startHours = Math.floor(row.timeRange[0] / 2);
                const startMinutes = (row.timeRange[0] % 2) * 30;
                const endHours = Math.floor(row.timeRange[1] / 2);
                const endMinutes = (row.timeRange[1] % 2) * 30;

                // Create time windows for all selected days in this row
                return row.daysOfWeek.map(dayOfWeek => {
                    // Create a date for the next occurrence of this day of week
                    const date = new Date();
                    date.setHours(0, 0, 0, 0);

                    // Set to the selected day of week
                    const currentDay = date.getDay();
                    const daysToAdd = dayOfWeek - currentDay;
                    date.setDate(date.getDate() + daysToAdd);

                    const startTime = new Date(date);
                    startTime.setHours(startHours, startMinutes, 0, 0);

                    const endTime = new Date(date);
                    endTime.setHours(endHours, endMinutes, 0, 0);

                    return {
                        startTime,
                        endTime,
                    };
                });
            });

            await batchUpsertMutation.mutateAsync(allTimeWindows);
        } catch {
            // Error toast handled by the mutation hook's onError
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddTimeWindow = () => {
        setTimeWindows(prev => [...prev, createDefaultTimeWindow()]);
    };

    const handleRemoveTimeWindow = (index: number) => {
        if (timeWindows.length > 1) {
            setTimeWindows(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleTimeWindowChange = (index: number, field: keyof TimeWindowRow, value: TimeWindowRow[keyof TimeWindowRow]) => {
        setTimeWindows(prev => prev.map((window, i) =>
            i === index ? { ...window, [field]: value } : window
        ));
    };

    if (readState === 'loading') {
        return <Box>{translate('time_windows.loading')}</Box>;
    }

    // `unreadable` and `never-issued` both mean the same thing to an operator
    // here: we do not know what this group's schedule is. The honest render is
    // to say so and withhold the form, because the form's only button
    // OVERWRITES that schedule. `never-issued` (the `Boolean(groupId)` gate
    // unsatisfied) is not reachable from `TimeWindowList`, which passes an id
    // off a listed row — it is handled rather than left to fall through for the
    // reason R98-1 gives: an entity-gated read gets a not-found or an error,
    // never a scope prompt, and never a blank editor over a live schedule.
    if (readState !== 'answered') {
        return <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />;
    }

    return (
        <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
                {timeWindows.map((timeWindow, index) => (
                    <Box key={timeWindow.id}>
                        {index > 0 && <Divider my={4} />}
                        <HStack spacing={4} align="flex-start">
                            <FormControl flex={2}>
                                <FormLabel>{translate('dayPicker.selectDaysOfWeek')}</FormLabel>
                                <MultiDayOfWeekPicker
                                    buttonWidth="100%"
                                    value={timeWindow.daysOfWeek}
                                    onChange={(newValue) => {
                                        handleTimeWindowChange(index, 'daysOfWeek', newValue as DayOfWeek[]);
                                    }}
                                />
                            </FormControl>

                            <FormControl flex={3}>
                                <FormLabel>{translate('time_windows.time_range')}</FormLabel>
                                <Box>
                                    <HStack justify="space-between" mb={2}>
                                        <Text fontSize="sm">{numberToHourMinuteLabel(timeWindow.timeRange[0])}</Text>
                                        <Text fontSize="sm">{numberToHourMinuteLabel(timeWindow.timeRange[1])}</Text>
                                    </HStack>
                                    <RangePicker
                                        labelsEveryXStep={2}
                                        step={1}
                                        defaultValue={timeWindow.timeRange}
                                        max={48}
                                        min={0}
                                        onChange={(newValues) => {
                                            handleTimeWindowChange(index, 'timeRange', [newValues[0], newValues[1]] as [number, number]);
                                        }}
                                    />
                                </Box>
                            </FormControl>

                            <IconButton
                                aria-label={translate('common.delete')}
                                icon={<DeleteIcon />}
                                onClick={() => handleRemoveTimeWindow(index)}
                                colorScheme="red"
                                variant="ghost"
                                isDisabled={timeWindows.length <= 1}
                                alignSelf="flex-end"
                                mt={8}
                            />
                        </HStack>
                    </Box>
                ))}

                <Button
                    leftIcon={<AddIcon />}
                    onClick={handleAddTimeWindow}
                    variant="ghost"
                    size="sm"
                >
                    {translate('time_windows.add_window')}
                </Button>

                <Button
                    type="submit"
                    colorScheme="brand"
                    isLoading={isSubmitting || batchUpsertMutation.isLoading}
                    w="full"
                >
                    {translate('save')}
                </Button>
            </VStack>
        </form>
    );
}